import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CopyToClipboard } from "react-copy-to-clipboard";

const generateLineNumbers = (text) => {
  if (typeof text !== "string") {
    console.error("Expected a string but received:", typeof text);
    return "";
  }
  const lines = text.split("\n").length;
  return Array.from({ length: lines }, (_, i) => i + 1).join("\n");
};

const formatCodeContent = (content) => {
  if (typeof content === "string") {
    return content;
  } else if (typeof content === "object") {
    try {
      return JSON.stringify(content, null, 2);
    } catch (error) {
      console.error("Failed to stringify content:", error);
      return "Error: Unable to display content";
    }
  }
  return String(content);
};

const CodeEditor = ({ codeContent, setCodeContent }) => {
  const [rawCodeContent, setRawCodeContent] = useState(formatCodeContent(codeContent));
  const [lineNumbers, setLineNumbers] = useState(generateLineNumbers(rawCodeContent));
    const [copied, setCopied] = useState(false);

  useEffect(() => {
    const formattedContent = formatCodeContent(codeContent);
    setRawCodeContent(formattedContent);
    setLineNumbers(generateLineNumbers(formattedContent));
  }, [codeContent]);

  const handleContentChange = (e) => {
    const updatedContent = e.target.value;
    setRawCodeContent(updatedContent);
    setCodeContent(updatedContent);
    setLineNumbers(generateLineNumbers(updatedContent));
  };

    const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
  };

  return (
    <Paper
      elevation={3}
      sx={{ padding: "20px", backgroundColor: "#3D3D3D", borderRadius: "4px" }}
    >
      <Box>
        {/* Top section with title and buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ color: "white", ml: 0.4 }}>Click to Edit</Typography>
          <CopyToClipboard text={rawCodeContent} onCopy={handleCopy}>
            <Tooltip title={copied ? "Copied!" : "Copy to clipboard"} arrow>
              <IconButton aria-label="copy" sx={{ color: "#FFFFFF" }}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </CopyToClipboard>
        </Box>

        {/* Code editor area with fixed line numbers */}
        <Box
          sx={{
            display: "flex",
            border: "1px solid #FFFFFF33",
            borderRadius: "4px",
            backgroundColor: "#282828",
            maxWidth: "100%",
          }}
        >
          {/* Line Numbers - Fixed to the left */}
          <Box
            sx={{
              padding: "5px",
              textAlign: "center",
              userSelect: "none",
              backgroundColor: "#3D3D3D",
              color: "#FFFFFF",
              fontFamily: "monospace",
              fontSize: "14px",
              minWidth: "40px",
              lineHeight: "20px",
              position: "sticky",
              left: 0,
              zIndex: 1,
            }}
          >
            <pre style={{ margin: 0, padding: 0, whiteSpace: "pre" }}>{lineNumbers}</pre>
          </Box>

          {/* Code Editor - Scrollable horizontally */}
          <Box sx={{ overflowX: "auto", flexGrow: 1, maxWidth: "100%" }} className="scrollbar-small">
            <TextField
              multiline
              fullWidth
              minRows={20}
              value={rawCodeContent}
              onChange={handleContentChange}
              InputProps={{
                style: {
                  fontFamily: "monospace",
                  fontSize: "14px",
                  lineHeight: "20px",
                  whiteSpace: "pre",
                  color: "#FFFFFF",
                  backgroundColor: "#282828",
                  minWidth: "1000px",
                  border: "none",  // Removes internal border
                },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  color: "#FFFFFF",
                  padding: "0px !important", // Ensures no extra padding

                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent !important", // Removes the white outline
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent !important", // Ensures no border on hover
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent !important", // No border when focused
                },
              }}
            />

          </Box>
        </Box>
      </Box>

      {/* Custom Scrollbar CSS */}
      <style>
        {`
          .scrollbar-small::-webkit-scrollbar {
            height: 6px; /* Reduce scrollbar height */
          }
          .scrollbar-small::-webkit-scrollbar-track {
            background: #3D3D3D; /* Dark background */
          }
          .scrollbar-small::-webkit-scrollbar-thumb {
            background: #888; /* Gray thumb */
            border-radius: 3px; /* Rounded scrollbar */
          }
          .scrollbar-small::-webkit-scrollbar-thumb:hover {
            background: #555; /* Darker on hover */
          }
        `}
      </style>
    </Paper>
  );
};

export default CodeEditor;
